import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  User,
  StudySession,
  MembershipPass,
  RealTimeStatsResponse,
} from '../types';
import { tokenManager } from '../infrastructure/api/tokenManager';

interface UserState {
  // 현재 사용자 정보
  currentUser: User | null;
  currentSession: StudySession | null;
  membershipPasses: MembershipPass[];
  realTimeStats: RealTimeStatsResponse | null;

  // 로딩 상태
  isLoading: boolean;

  // 액션
  setCurrentUser: (user: User | null) => void;
  setCurrentSession: (session: StudySession | null) => void;
  setMembershipPasses: (passes: MembershipPass[]) => void;
  setRealTimeStats: (stats: RealTimeStatsResponse | null) => void;
  setLoading: (loading: boolean) => void;
  login: (
    phoneOrEmail: string,
    password: string,
    type: 'user' | 'owner' | 'admin'
  ) => Promise<boolean>;

  // 유틸리티
  getActivePass: () => MembershipPass | null;
  hasActiveSession: () => boolean;
  isOwner: () => boolean;
  logout: () => void;
}

// [2026-01-05] Admin 세션 복구: admin_user가 있으면 user-storage 덮어쓰기
if (typeof window !== 'undefined') {
  const adminUser = localStorage.getItem('admin_user');
  const adminToken = localStorage.getItem('admin_token');

  if (adminUser && adminToken) {
    try {
      const user = JSON.parse(adminUser);
      // admin_token이 있으면 role을 항상 'admin'으로 강제 설정
      user.role = 'admin';
      // admin 사용자 데이터로 user-storage 업데이트
      const userStorage = {
        state: {
          currentUser: user,
          currentSession: null,
          membershipPasses: [],
        },
        version: 0,
      };
      localStorage.setItem('user-storage', JSON.stringify(userStorage));
      console.log('✅ [userStore] Admin 세션 복구:', user.email, user.role);
    } catch (error) {
      console.error('❌ [userStore] Admin 세션 복구 실패:', error);
    }
  }
}

// 개발 모드에서 localStorage 초기화 (필요시)
if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
  const stored = localStorage.getItem('user-storage');
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      if (!parsed.state || !parsed.state.currentUser) {
        console.log(
          '🔧 [DEV] currentUser가 없어서 localStorage를 초기화합니다.'
        );
        localStorage.removeItem('user-storage');
      }
    } catch (error) {
      localStorage.removeItem('user-storage');
    }
  }
}

export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      // 초기 상태
      currentUser: null,
      currentSession: null,
      membershipPasses: [],
      realTimeStats: null,
      isLoading: false,

      // 액션
      setCurrentUser: (user) => set({ currentUser: user }),

      setCurrentSession: (session) => set({ currentSession: session }),

      setMembershipPasses: (passes) => set({ membershipPasses: passes }),

      setRealTimeStats: (stats) => set({ realTimeStats: stats }),

      setLoading: (loading) => set({ isLoading: loading }),

      // 로그인 함수
      login: async (
        phoneOrEmail: string,
        password: string,
        type: 'user' | 'owner' | 'admin'
      ) => {
        set({ isLoading: true });

        try {
          const { authApi } = await import('../services/authApi');
          
          // 전화번호인지 이메일인지 확인
          const isPhone = /^[0-9-]+$/.test(phoneOrEmail.replace(/\s/g, ''));
          let loginResponse;

          if (isPhone) {
            // 전화번호로 로그인
            loginResponse = await authApi.loginByPhone({ phone: phoneOrEmail, password });
          } else {
            // 이메일로 로그인
            const credentials = { email: phoneOrEmail, password };
            if (type === 'owner') {
              loginResponse = await authApi.ownerLogin(credentials);
            } else {
              loginResponse = await authApi.login(credentials);
            }
          }

          // 로그인 성공
          const { user, access_token, refresh_token } = loginResponse;


          // 🧹 이전 토큰 모두 정리 (충돌 방지)
          tokenManager.clearAllTokens();

          // 🧹 이전 사용자/조직 캐시 정리 (다른 사용자로 로그인 시 사이드바에 옛 조직명 노출 방지)
          localStorage.removeItem('cached_organization_info');
          localStorage.removeItem('user');
          localStorage.removeItem('organization_id');
          localStorage.removeItem('sessionId');

          set({ currentUser: user });

          // 🔧 AuthLocalStorageAdapter 키 이름으로 통일
          localStorage.setItem('auth_access_token', access_token);
          localStorage.setItem('accessToken', access_token); // 호환성 유지
          localStorage.setItem('token', access_token); // CounselingInsightPage 등에서 사용

          if (refresh_token) {
            localStorage.setItem('auth_refresh_token', refresh_token);
            localStorage.setItem('refreshToken', refresh_token); // 호환성 유지
            // expiresAt 계산 (Supabase 토큰은 보통 1시간)
            const expiresAt = new Date(Date.now() + 3600 * 1000).toISOString();
            localStorage.setItem('auth_token_expires_at', expiresAt);
          } else {
            console.error('❌ No refresh_token to save!');
          }

          localStorage.setItem('authToken', access_token); // 기존 토큰명도 호환성을 위해 유지

          // 🔧 organization_id 저장 (상담기록 API에서 필요)
          if (user.organization_id) {
            localStorage.setItem('organization_id', user.organization_id);
          }

          // 🔧 새 조직 정보 캐싱 (사이드바 즉시 갱신)
          if ((user as any).organization_name && user.organization_id) {
            const orgInfo = {
              id: user.organization_id,
              name: (user as any).organization_name,
              description: 'NewAttend 시스템으로 운영되는 스마트 독서실입니다.',
              settings: {},
            };
            localStorage.setItem('cached_organization_info', JSON.stringify(orgInfo));
            window.dispatchEvent(new Event('organizationNameUpdated'));
          }

          // 저장 확인
          const savedAccess = localStorage.getItem('auth_access_token');
          const savedRefresh = localStorage.getItem('auth_refresh_token');

          return true;
        } catch (error) {
          console.error('❌ 로그인 실패:', error);
          
          // API 호출 실패 시 에러 메시지 표시
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // 유틸리티 함수
      getActivePass: () => {
        const { membershipPasses } = get();
        const now = new Date();

        return (
          membershipPasses.find(
            (pass) =>
              pass.is_active &&
              new Date(pass.expires_at) > now &&
              pass.remaining_minutes > 0
          ) || null
        );
      },

      hasActiveSession: () => {
        const { currentSession } = get();
        return currentSession?.status === 'active';
      },

      isOwner: () => {
        const { currentUser } = get();
        return currentUser?.role === 'owner' || currentUser?.role === 'admin';
      },

      logout: () => {
        set({
          currentUser: null,
          currentSession: null,
          membershipPasses: [],
          realTimeStats: null,
        });
        localStorage.removeItem('authToken');
      },
    }),
    {
      name: 'user-storage',
      partialize: (state) => ({
        currentUser: state.currentUser,
        currentSession: state.currentSession,
        membershipPasses: state.membershipPasses,
      }),
    }
  )
);
